/**
 * Auto-dim — `userSettings/{uid}.simpleDashboard.nightBrightness`.
 *
 * Contract shared with the clock (adhan2) and the iOS/Android apps:
 *   { enabled, dimLevelPercent: 10..100, dayLevelPercent: dimLevelPercent..100,
 *     dimTrigger, restoreTrigger }   (trigger shape as in screenSchedule.js)
 * Missing block → disabled, dim 70 %, day 100 %, dim at Isha, restore at Fajr.
 * The clock also reads lower-case "clockTime"/"time" and an old `startOffsetMinutes`;
 * writers write only the shape above with upper-case trigger types.
 */
import { parseTrigger, serializeTrigger } from './screenSchedule';

export const MIN_LEVEL_PERCENT = 10;
export const MAX_LEVEL_PERCENT = 100;

export const DEFAULT_NIGHT_BRIGHTNESS = Object.freeze({
  enabled: false,
  dimLevelPercent: 70,
  dayLevelPercent: 100,
  dimTrigger: Object.freeze({ type: 'PRAYER', prayer: 'Isha', offsetMinutes: 0 }),
  restoreTrigger: Object.freeze({ type: 'PRAYER', prayer: 'Fajr', offsetMinutes: 0 }),
});

const isRecord = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const toInt = (v) => (typeof v === 'number' && Number.isFinite(v) ? Math.round(v) : null);
const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

/** Clamped levels: dim in 10..100, day in dim..100 (missing values → defaults). */
export function clampLevels(dimRaw, dayRaw) {
  const dim = clamp(toInt(dimRaw) ?? DEFAULT_NIGHT_BRIGHTNESS.dimLevelPercent, MIN_LEVEL_PERCENT, MAX_LEVEL_PERCENT);
  const day = clamp(toInt(dayRaw) ?? DEFAULT_NIGHT_BRIGHTNESS.dayLevelPercent, dim, MAX_LEVEL_PERCENT);
  return { dimLevelPercent: dim, dayLevelPercent: day };
}

// Old documents carry the offset as `startOffsetMinutes`.
const withLegacyOffset = (raw) =>
  isRecord(raw) && toInt(raw.offsetMinutes) === null && toInt(raw.startOffsetMinutes) !== null
    ? { ...raw, offsetMinutes: raw.startOffsetMinutes }
    : raw;

/** Firestore value (possibly missing/partial) → full settings object for the UI. */
export function parseNightBrightness(raw) {
  const block = isRecord(raw) ? raw : {};
  return {
    enabled: block.enabled === true,
    ...clampLevels(block.dimLevelPercent, block.dayLevelPercent),
    dimTrigger: parseTrigger(withLegacyOffset(block.dimTrigger), DEFAULT_NIGHT_BRIGHTNESS.dimTrigger),
    restoreTrigger: parseTrigger(withLegacyOffset(block.restoreTrigger), DEFAULT_NIGHT_BRIGHTNESS.restoreTrigger),
  };
}

/** UI settings → the exact map written to `simpleDashboard.nightBrightness` (all five fields). */
export function serializeNightBrightness(settings) {
  const s = isRecord(settings) ? settings : {};
  return {
    enabled: s.enabled === true,
    ...clampLevels(s.dimLevelPercent, s.dayLevelPercent),
    dimTrigger: serializeTrigger(withLegacyOffset(s.dimTrigger), DEFAULT_NIGHT_BRIGHTNESS.dimTrigger),
    restoreTrigger: serializeTrigger(withLegacyOffset(s.restoreTrigger), DEFAULT_NIGHT_BRIGHTNESS.restoreTrigger),
  };
}
